import { Box, Step, StepButton, Stepper, Typography } from '@mui/material'
import {
    groupActiveStep,
    requiredProgress,
    requiredSectionsArePrefix,
    sectionIsComplete,
    sectionIsEditable,
    type WorkbookStepperSection,
} from './workbookStepper'

const stepperSx = {
    '& .MuiStepLabel-label': { color: '#8b949e' },
    '& .MuiStepLabel-label.Mui-active': { color: '#fff' },
    '& .MuiStepLabel-label.Mui-completed': { color: '#46b083' },
    '& .MuiStepIcon-root': { color: 'rgba(139, 148, 158, 0.45)' },
    '& .MuiStepIcon-root.Mui-active': { color: '#46b083' },
    '& .MuiStepIcon-root.Mui-completed': { color: '#46b083' },
    '& .MuiStepConnector-line': {
        borderColor: 'rgba(139, 148, 158, 0.35)',
    },
} as const

function stepOptional(
    section: WorkbookStepperSection,
    showOptional: boolean,
) {
    if (sectionIsComplete(section) && sectionIsEditable(section)) {
        return (
            <Typography variant="caption" sx={{ color: '#46b083' }}>
                Editable
            </Typography>
        )
    }
    if (showOptional && !section.required) {
        return (
            <Typography variant="caption" sx={{ color: '#8b949e' }}>
                Optional
            </Typography>
        )
    }
    return undefined
}

function SectionStepper({
    sections,
    startIndex,
    activeStep,
    onGoToSection,
    showOptionalCaptions,
}: {
    sections: WorkbookStepperSection[]
    startIndex: number
    activeStep: number
    onGoToSection: (index: number) => void
    showOptionalCaptions: boolean
}) {
    if (sections.length === 0) return null
    return (
        <Stepper
            activeStep={groupActiveStep(activeStep, startIndex, sections.length)}
            alternativeLabel
            nonLinear
            sx={stepperSx}
        >
            {sections.map((section, groupIndex) => {
                const index = startIndex + groupIndex
                return (
                    <Step
                        key={section.id}
                        completed={sectionIsComplete(section)}
                    >
                        <StepButton
                            onClick={() => onGoToSection(index)}
                            optional={stepOptional(
                                section,
                                showOptionalCaptions,
                            )}
                        >
                            {section.label}
                        </StepButton>
                    </Step>
                )
            })}
        </Stepper>
    )
}

export function ApplicantWorkbookStepper({
    sections,
    activeStep,
    remainderOnly,
    isSubmitted = false,
    onGoToSection,
}: {
    sections: WorkbookStepperSection[]
    activeStep: number
    remainderOnly: boolean
    isSubmitted?: boolean
    onGoToSection: (index: number) => void
}) {
    const progress =
        remainderOnly || isSubmitted
            ? {
                  required: [] as WorkbookStepperSection[],
                  completeCount: 0,
                  headline: null,
                  countLabel: null,
              }
            : requiredProgress(sections)
    const splitGroups = !remainderOnly && requiredSectionsArePrefix(sections)
    const requiredSections = sections.filter((section) => section.required)
    const requiredCount = requiredSections.length
    const optionalSections = splitGroups ? sections.slice(requiredCount) : []
    const requiredComplete =
        requiredCount > 0 && requiredSections.every(sectionIsComplete)
    const showStepper = sections.length > 1

    if (!progress.headline && !showStepper) return null

    return (
        <Box sx={{ mb: 3 }}>
            {progress.headline && (
                <Box sx={{ mb: showStepper ? 2 : 0 }}>
                    <Typography
                        sx={{
                            color: requiredComplete ? '#46b083' : '#fff',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                        }}
                    >
                        {progress.headline}
                    </Typography>
                    {progress.countLabel && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: requiredComplete ? '#46b083' : '#8b949e',
                                mt: 0.5,
                            }}
                        >
                            {progress.countLabel}
                        </Typography>
                    )}
                </Box>
            )}

            {showStepper && splitGroups ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'flex-start' },
                        gap: { xs: 1.5, md: 0 },
                    }}
                >
                    <Box sx={{ flex: requiredCount, minWidth: 0 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                textAlign: 'center',
                                color: '#fff',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                mb: 1,
                            }}
                        >
                            Required to continue
                        </Typography>
                        <SectionStepper
                            sections={requiredSections}
                            startIndex={0}
                            activeStep={activeStep}
                            onGoToSection={onGoToSection}
                            showOptionalCaptions={false}
                        />
                    </Box>
                    <Box
                        aria-hidden
                        sx={{
                            alignSelf: 'stretch',
                            borderColor: 'rgba(139, 148, 158, 0.35)',
                            borderStyle: 'solid',
                            borderWidth: { xs: '1px 0', md: '0 1px' },
                            minHeight: { xs: 12, md: 0 },
                            minWidth: { md: 16 },
                            mx: { md: 1.5 },
                            my: { xs: 0.5, md: 1 },
                        }}
                    />
                    <Box sx={{ flex: Math.max(optionalSections.length, 1), minWidth: 0 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                textAlign: 'center',
                                color: '#8b949e',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                mb: 1,
                            }}
                        >
                            Optional now
                        </Typography>
                        <SectionStepper
                            sections={optionalSections}
                            startIndex={requiredCount}
                            activeStep={activeStep}
                            onGoToSection={onGoToSection}
                            showOptionalCaptions={false}
                        />
                    </Box>
                </Box>
            ) : showStepper ? (
                <SectionStepper
                    sections={sections}
                    startIndex={0}
                    activeStep={activeStep}
                    onGoToSection={onGoToSection}
                    showOptionalCaptions={
                        !remainderOnly &&
                        requiredCount > 0 &&
                        requiredCount < sections.length
                    }
                />
            ) : null}
        </Box>
    )
}
